import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'react-bootstrap';
import SSTable from '../SSTable/SSTable';
import SSColumn from '../SSTable/SSColumn';
import ToolTip from '../ToolTipIcon/ToolTip';
import {
  getIdNode,
  getLocalTime,
  getOnlineStatusIcon,
  downloadStatusToIconSrc,
  getFormatDistanceToNow,
  getDownloadStatusText,
} from '../Devices/DeviceUtils';
import { classNames } from '../../utils/v4/utils';

export default class DashboardTable extends Component {
  state = {
    open: false,
  };

  getIcons = device => (
    <div className="device-icons">
      {getOnlineStatusIcon(device)}
      <ToolTip toolText={getDownloadStatusText(device)}>
        <img
          data-test="icon-download-status"
          src={downloadStatusToIconSrc(device)}
          alt="downloading status"
          className={classNames(
            'w-[30px] h-[30px] inline-block',
            device.download_status === 'downloading' ? 'animate-pulse' : '',
            device.idle ? '' : 'opacity-20',
            device.download_status,
          )}
        />
      </ToolTip>
    </div>
  );

  getDevice = device => {
    const location = device.location_path === '/' ? 'No Location set' : device.location_path;

    return (
      <div>
        {getIdNode(device, this.props.users)} {device.description}
        <div className="location">
          <i className="material-icons" title="Location">
            public
          </i>
          {location}
        </div>
      </div>
    );
  };

  getDistanceToNowWithToolTip = date => (
    <ToolTip toolText={getLocalTime(date)}>{getFormatDistanceToNow(date)}</ToolTip>
  );

  getOnlineStatusText = device =>
    device.idle ? this.getDistanceToNowWithToolTip(Date.now() + device.idle * 1000) : 'Never seen';

  getModifiedDate = device =>
    device.modifiedDate && new Date(device.modifiedDate).getTime()
      ? this.getDistanceToNowWithToolTip(device.modifiedDate)
      : 'N/A';

  getCreatedDate = device =>
    device.createdDate && new Date(device.createdDate).getTime()
      ? this.getDistanceToNowWithToolTip(device.createdDate)
      : 'N/A';

  getContentDateDistanceToNow = device =>
    Number(device.content_ver)
      ? this.getDistanceToNowWithToolTip(device.content_ver * 1000)
      : 'N/A';

  getDownloadingContentDateDistanceToNow = device =>
    device.downloading_ver
      ? this.getDistanceToNowWithToolTip(device.downloading_ver * 1000)
      : 'N/A';

  getTypeData = () =>
    ({
      offlineDevices: {
        icon: <i className="material-icons material-icons offline">lens</i>,
        title: 'Latest offline devices',
        header: 'Last seen',
        cell: this.getOnlineStatusText,
      },
      modifiedDevices: {
        icon: <i className="material-icons material-icons">mode_edit</i>,
        title: 'Latest modified devices',
        header: 'Modified',
        cell: this.getModifiedDate,
      },
      registeredDevices: {
        icon: <i className="material-icons material-icons">add_circle</i>,
        title: 'Latest registered devices',
        header: 'Registered',
        cell: this.getCreatedDate,
      },
      outdatedContent: {
        icon: (
          <img
            src="/assets/images/icons/outdated_content.svg"
            alt="outdated status"
            className="w-[30px] img-titles h-[30px] mr-[1px] inline-flex"
          />
        ),
        title: 'Devices with outdated content',
        header: 'Content deployed',
        cell: this.getContentDateDistanceToNow,
      },
      newContent: {
        icon: (
          <img
            src="/assets/images/icons/up_to_date_content.svg"
            alt="up to date status"
            className="w-[30px] img-titles mr-[1px] h-[30px] inline-flex"
          />
        ),
        title: 'Devices with latest deployed content',
        header: 'Content deployed',
        cell: this.getContentDateDistanceToNow,
      },
      downloadingContent: {
        icon: (
          <img
            src="/assets/images/icons/up_to_date_content.svg"
            alt="downloading status"
            className="w-[30px] img-titles animate-pulse mr-[1px] h-[30px] downloading inline-flex"
          />
        ),
        title: 'Devices are downloading content',
        header: 'Content deployed',
        cell: this.getDownloadingContentDateDistanceToNow,
      },
    })[this.props.type];

  toggle = () => this.setState(prevState => ({ open: !prevState.open }));

  render() {
    const { users, data = [] } = this.props;
    const { open } = this.state;
    const { icon, title, header, cell } = this.getTypeData();
    const dataExists = Boolean(users && data?.length);
    const slicedData = open ? data : data.slice(0, 5);

    return (
      <Panel data-test={`panel-${title.replace(/\s/g, '')}`}>
        <Panel.Heading className="toggle" onClick={this.toggle}>
          <ToolTip toolText={`Click to show ${open ? 'latest 5' : 'all'} devices`}>
            {icon}
            <div className="flex items-center  space-x-2">
              <i className="material-icons material-caret inline ">
                {open ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
              </i>
              <span className="font-bold">{title}</span>
            </div>
          </ToolTip>
        </Panel.Heading>
        <Panel.Body className={open ? 'open' : ''}>
          {dataExists && (
            <SSTable
              className="responsive"
              data={slicedData}
              headerHeight={25}
              rowHeight={45}
              rowsCount={slicedData.length - 1}
              zebra
            >
              <SSColumn containerClasses="!w-[80px] " header="" cell={this.getIcons} />
              <SSColumn header="Device" cell={this.getDevice} />
              <SSColumn containerClasses="!w-[110px] " header={header} cell={cell} />
            </SSTable>
          )}
        </Panel.Body>
      </Panel>
    );
  }
}

DashboardTable.propTypes = {
  type: PropTypes.string,
  users: PropTypes.array,
  data: PropTypes.array,
};
